import { DeleteOutlined } from '@ant-design/icons'
import { useMutation } from '@apollo/client'
import { Button, Checkbox, Col, Form, Input, message, Popconfirm, Row, Spin } from 'antd'
import React, { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useLoginModal } from '../../../../context/loginModalContext'
import { useUser } from '../../../../context/userContext'
import { showAuthError } from '../../../../shared/utils/webHelpers'
import { StyledDrawer } from '../../../common/CommonStyles'
import { useForceUpdate } from '../../../../shared/hooks/useForceUpdate'
import { v4 as uuidv4 } from 'uuid'
import CreateNewItemExtraButtons from '../../../common/CreateNewItemExtraButtons'
import EDIT_INSURANCE_POLICY from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/insurancePolicies/editInsurancePolicy'
import DELETE_INSURANCE_POLICY from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/insurancePolicies/deleteInsurancePolicy'
import ADD_NEW_INSURANCE_POLICY from '@dentolize/clinic-mobile/src/shared/store/mutations/actions/insurancePolicies/addNewInsurancePolicy'
import SearchField from '../../../common/searchFields/SearchField'
import { SEARCH_PRICE_LISTS_QUERY } from '@dentolize/clinic-mobile/src/shared/store/queries/appointmentQueries'
import { selectorDateFormat } from '../../settings/Print/formHelpers'
import SelectWithNewOptions from '../../../common/formFields/SelectWithNewOptions'
import moment from 'moment'
import {
  coverageRelationships,
  coverageTypes
} from '@dentolize/clinic-mobile/src/components/dashboard/More/associates/insurancePolicies/insurancePolicyUtils'
import MomentDatePicker from '../../../../utils/timezone/MomentDatePicker'

const InsurancePolicyForm = props => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { user } = useUser()
  const { insuranceCompany } = useParams()
  const { setLoginModalVisible } = useLoginModal()
  const [forceUpdate, handleForceUpdate] = useForceUpdate()

  const { formVisible, selectedItem, handleClose } = props
  const { validateFields, resetFields, setFieldsValue } = form

  const generatedID = useMemo(uuidv4, [formVisible, forceUpdate])

  useEffect(() => {
    resetFields()
  }, [formVisible])

  const [addInsurancePolicy, { loading }] = useMutation(ADD_NEW_INSURANCE_POLICY)
  const [editInsurancePolicy, { loading: loadingEdit }] = useMutation(EDIT_INSURANCE_POLICY)
  const [deleteInsurancePolicy, { loading: loadingDelete }] = useMutation(DELETE_INSURANCE_POLICY, {
    update: handleUpdateAfterDelete
  })

  function handleUpdateAfterDelete(store, deletedItem) {
    store.data.delete(`InsurancePolicy:${deletedItem.data.deleteInsurancePolicy.id}`)
    props.handleAnimateRow && props.handleAnimateRow(null, true)
  }

  const handleSubmit = key => {
    validateFields()
      .then(async values => {
        try {
          const variables = {
            ...values,
            priceList: values.priceList?.key
          }

          let res

          if (selectedItem.new) {
            res = await addInsurancePolicy({
              variables: {
                ...variables,
                insuranceCompany: insuranceCompany || selectedItem.insuranceCompany
              }
            })
            if (key === 'view') {
              navigate(`/associates/insurance-policy/${res.data.addNewInsurancePolicy.id}`)
              handleClose()
            } else if (key === 'new') {
              resetFields()
              handleForceUpdate()
            } else {
              handleClose(res.data.addNewInsurancePolicy)
            }
          } else {
            res = await editInsurancePolicy({
              variables: {
                ...variables,
                insurancePolicy: selectedItem.id
              }
            })
            handleClose()
          }

          message.success(`${selectedItem.new ? t('common.createdSuccessfully') : t('common.updatedSuccessfully')}`)

          if (props.handleAnimateRow) {
            props.handleAnimateRow(
              (res.data.addNewInsurancePolicy && res.data.addNewInsurancePolicy.id) ||
                (res.data.editInsurancePolicy && res.data.editInsurancePolicy.id),
              selectedItem.new
            )
          }
        } catch (e) {
          showAuthError(e, t, setLoginModalVisible)
        }
      })
      .catch(({ errorFields }) => {
        form.scrollToField(errorFields?.[0]?.name)
      })
  }

  const handleDelete = async () => {
    try {
      await deleteInsurancePolicy({
        variables: { insurancePolicy: selectedItem.id }
      })
      message.success(t('common.deletedSuccess'))
      handleClose()
      if (!insuranceCompany) {
        navigate(-1)
      }
    } catch (e) {
      showAuthError(e, t, setLoginModalVisible)
    }
  }

  return (
    <StyledDrawer
      title={selectedItem.new ? t('patients.newItem') : `${t('patients.editItem')} - (${selectedItem.holderName})`}
      open={formVisible}
      onClose={handleClose}
      destroyOnClose
      footer={
        <div className="flex-container justify-between">
          {!selectedItem.new && user.permissions.deleteInsuranceCompany ? (
            <Popconfirm
              title={t('settings.insurance.sureDelete')}
              okText={t('common.yes')}
              cancelText={t('common.no')}
              onConfirm={handleDelete}
              disabled={loading || loadingEdit}
            >
              <Button loading={loadingDelete} disabled={loading || loadingEdit} type="primary" danger icon={<DeleteOutlined />}>
                {t('common.delete')}
              </Button>
            </Popconfirm>
          ) : selectedItem.new && selectedItem.fromDrawer ? (
            <CreateNewItemExtraButtons handleSubmit={handleSubmit} disabled={loading} />
          ) : (
            <span />
          )}
          <Button
            loading={loading || loadingEdit}
            disabled={loadingDelete}
            htmlType="submit"
            type="primary"
            form={`${generatedID}_InsurancePolicyForm`}
          >
            {selectedItem.new ? t('common.create') : t('common.update')}
          </Button>
        </div>
      }
    >
      <Spin spinning={loading || loadingDelete || loadingEdit}>
        <Form
          layout="vertical"
          scrollToFirstError
          form={form}
          name={`${generatedID}_InsurancePolicyForm`}
          onFinish={handleSubmit}
          initialValues={{
            ...selectedItem,
            updatePatients: true,
            due: selectedItem.due ? moment(selectedItem.due) : null,
            inception: selectedItem.inception ? moment(selectedItem.inception) : null,
            priceList: selectedItem.priceList ? { label: selectedItem.priceList.name, key: selectedItem.priceList.id } : null
          }}
        >
          <Row gutter={16}>
            <Col md={12} xs={24}>
              <Form.Item label={t('common.name')} name="holderName" rules={[{ required: true, message: t('app.fieldRequired') }]}>
                <Input maxLength={100} autoFocus autoComplete="off" />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                label={t('settings.insurance.policyNumber')}
                name="policyNumber"
                rules={[{ required: true, message: t('app.fieldRequired') }]}
              >
                <Input maxLength={100} autoComplete="off" />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <SelectWithNewOptions
                label={t('settings.insurance.coverageType')}
                rules={[{ max: 100 }]}
                name="coverageType"
                hasFeedback
                allowClear
                setFieldsValue={setFieldsValue}
                options={coverageTypes}
                formName="insurancePolicyForm"
              />
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('settings.insurance.startDate')} name="inception">
                <MomentDatePicker
                  data-testid="InsurancePolicyForm-inception"
                  allowClear
                  format={{
                    format: selectorDateFormat(user)[1],
                    type: 'mask'
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item
                label={t('settings.insurance.endDate')}
                name="due"
                rules={[{ required: true, message: t('app.fieldRequired') }]}
              >
                <MomentDatePicker
                  data-testid="InsurancePolicyForm-due"
                  format={{
                    format: selectorDateFormat(user)[1],
                    type: 'mask'
                  }}
                />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <SelectWithNewOptions
                label={t('patients.relationship')}
                rules={[{ max: 100 }]}
                name="relationship"
                hasFeedback
                allowClear
                setFieldsValue={setFieldsValue}
                options={coverageRelationships}
                formName="insurancePolicyForm"
              />
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('settings.insurance.sponsorNumber')} name="sponsorNumber">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <Form.Item label={t('calendar.status')} name="status">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col md={12} xs={24}>
              <SearchField
                formName="insurancePolicyForm"
                GET_QUERY={SEARCH_PRICE_LISTS_QUERY}
                label={t('settings.branches.defaultPriceList')}
                fieldName="priceList"
                dataName="searchPriceLists"
                allowClear
              />
            </Col>
            <Col span={24}>
              <br />
              <Form.Item label={t('common.details')} name="details">
                <Input.TextArea rows={4} maxLength={1000} showCount />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="disabled" valuePropName="checked">
                <Checkbox>{t('common.disabled')}</Checkbox>
              </Form.Item>
            </Col>
            {!selectedItem.new ? (
              <Col span={24}>
                <Form.Item name="updatePatients" valuePropName="checked" extra={t('settings.insurance.updatePatientsHelp')}>
                  <Checkbox>{t('settings.insurance.updatePatients')}</Checkbox>
                </Form.Item>
              </Col>
            ) : null}
          </Row>
        </Form>
      </Spin>
    </StyledDrawer>
  )
}

export default InsurancePolicyForm
